import { html, TemplateResult } from 'lit';
import type { RecordSyncStatus } from '@src/modules/kernel/PendingSyncRecord.ts';
import { shortPublicId } from '@src/modules/kernel/formDisplayHelpers.ts';
import { shellSectionClass, shellEmptyState } from './shellContentUi.ts';

export type RecordListSyncFilter = 'all' | RecordSyncStatus;

export function filterRecordListRows<T extends { syncStatus: RecordSyncStatus }>(
  rows: readonly T[],
  query: string,
  syncFilter: RecordListSyncFilter,
  searchText: (row: T) => string,
): T[] {
  let out = [...rows];
  if (syncFilter !== 'all') out = out.filter((row) => row.syncStatus === syncFilter);
  const q = query.trim().toLowerCase();
  if (q) out = out.filter((row) => searchText(row).toLowerCase().includes(q));
  return out;
}

export function syncStatusLabel(status: RecordSyncStatus): string {
  if (status === 'synced') return 'Sincronizada';
  if (status === 'failed') return 'Error al sincronizar';
  return 'Pendiente';
}

export function shellSyncStatusBadge(status: RecordSyncStatus): TemplateResult {
  if (status === 'pending') {
    return html`<span class="shell-sync-badge shell-sync-badge--pending">${syncStatusLabel(status)}</span>`;
  }
  const tone =
    status === 'synced'
      ? 'shell-sync-badge shell-sync-badge--synced'
      : 'shell-sync-badge shell-sync-badge--failed';
  return html`<span class="${tone}">${syncStatusLabel(status)}</span>`;
}

export function shellRecordDetailBadges(detail: string): TemplateResult {
  const parts = detail
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const products = parts.length > 0 && parts.every((p) => /×\d+\s*$/.test(p));
  if (!products) {
    return html`<p class="shell-record-card__detail-text">${detail}</p>`;
  }
  return html`
    <div class="shell-record-card__badges" role="list" aria-label="Productos">
      ${parts.map((part) => html`<span class="shell-record-card__badge" role="listitem">${part}</span>`)}
    </div>
  `;
}

export function shellRecordContextBadges(medioNombre?: string, motivoNombre?: string): TemplateResult | null {
  const items: Array<{ label: string; value: string }> = [];
  if (medioNombre && medioNombre !== '—') items.push({ label: 'Medio', value: medioNombre });
  if (motivoNombre && motivoNombre !== '—') items.push({ label: 'Motivo', value: motivoNombre });
  if (items.length === 0) return null;
  return html`
    <div class="shell-record-card__context" role="list" aria-label="Medio de reporte y motivo">
      ${items.map(
        (item) => html`
          <span class="shell-record-card__context-badge" role="listitem">
            <span class="shell-record-card__context-label">${item.label}</span>
            <span class="shell-record-card__context-value">${item.value}</span>
          </span>
        `,
      )}
    </div>
  `;
}

function formatRecordCardDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
}

function shellRecordCardChevron(): TemplateResult {
  return html`
    <svg
      class="shell-record-card__chevron h-5 w-5 shrink-0 text-[color:var(--md-sys-color-on-surface-variant)]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      aria-hidden="true"
    >
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  `;
}

function shellRecordListCardContent(
  row: {
    fecha: string;
    syncStatus: RecordSyncStatus;
    syncError?: string | null;
    headline: string;
    subline: string;
    detail?: string;
    medioNombre?: string;
    motivoNombre?: string;
    publicId: string;
  },
  showChevron: boolean,
): TemplateResult {
  return html`
    <div class="shell-record-card__body">
      <div class="shell-record-card__header">
        <p class="shell-record-card__headline">${row.headline}</p>
        ${shellSyncStatusBadge(row.syncStatus)}
      </div>
      <p class="shell-record-card__subline">${row.subline}</p>
      ${shellRecordContextBadges(row.medioNombre, row.motivoNombre)}
      ${row.detail ? shellRecordDetailBadges(row.detail) : null}
      ${row.syncError
        ? html`<p
            class="shell-record-card__error ${row.syncStatus === 'failed' ? '' : 'shell-record-card__error--warning'}"
            role=${row.syncStatus === 'failed' ? 'alert' : 'status'}
          >
            ${row.syncError}
          </p>`
        : null}
    </div>
    <div class="shell-record-card__footer">
      <time class="shell-record-card__date" datetime=${row.fecha}>${formatRecordCardDate(row.fecha)}</time>
      <span class="shell-record-card__ref" title=${row.publicId}>Ref. ${shortPublicId(row.publicId)}</span>
      ${showChevron ? shellRecordCardChevron() : null}
    </div>
  `;
}

const SYNC_FILTER_OPTIONS: Array<{ value: RecordListSyncFilter; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'synced', label: 'Enviadas' },
  { value: 'failed', label: 'Con error' },
];

function shellRecordListFilterToolbar(p: {
  query: string;
  syncFilter: RecordListSyncFilter;
  onQueryChange: (value: string) => void;
  onSyncFilterChange: (value: RecordListSyncFilter) => void;
}): TemplateResult {
  return html`
    <div class="mb-4 space-y-3">
      <label class="block">
        <span class="sr-only">Buscar registros</span>
        <input
          type="search"
          class="md-field__input input-md w-full min-h-[2.75rem] rounded-[var(--md-shape-corner-medium)] border px-4 py-2.5 text-base outline-none"
          placeholder="Buscar por nombre, lugar o fecha…"
          .value=${p.query}
          @input=${(e: Event) => p.onQueryChange((e.target as HTMLInputElement).value)}
        />
      </label>
      <div class="flex flex-wrap gap-2" role="group" aria-label="Filtrar por estado de sincronización">
        ${SYNC_FILTER_OPTIONS.map(
          (opt) => html`
            <button
              type="button"
              class="shell-filter-chip md-sys-ripple touch-manipulation ${p.syncFilter === opt.value ? 'shell-filter-chip--active' : ''}"
              aria-pressed=${p.syncFilter === opt.value ? 'true' : 'false'}
              @click=${() => p.onSyncFilterChange(opt.value)}
            >
              ${opt.label}
            </button>
          `,
        )}
      </div>
    </div>
  `;
}

export function shellModuleRecordListSection(p: {
  title: string;
  emptyText: string;
  newLabel?: string;
  onNew?: () => void;
  showCount?: boolean;
  clickable?: boolean;
  filter?: {
    query: string;
    syncFilter: RecordListSyncFilter;
    onQueryChange: (value: string) => void;
    onSyncFilterChange: (value: RecordListSyncFilter) => void;
  };
  rows: Array<{
    fecha: string;
    syncStatus: RecordSyncStatus;
    syncError?: string | null;
    headline: string;
    subline: string;
    detail?: string;
    medioNombre?: string;
    motivoNombre?: string;
    publicId: string;
    onOpen?: () => void;
    actions?: TemplateResult;
  }>;
}): TemplateResult {
  const clickable = p.clickable ?? true;
  const emptyFiltered = p.rows.length === 0 && p.filter && (p.filter.query.trim() || p.filter.syncFilter !== 'all');
  return html`
    <section class="${shellSectionClass}">
      <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-base font-medium text-[color:var(--md-sys-color-on-surface)]">
          ${p.title}
          ${p.showCount
            ? html`<span class="ml-1 text-sm font-normal text-[color:var(--md-sys-color-on-surface-variant)]">(${p.rows.length})</span>`
            : null}
        </h2>
        ${p.newLabel && p.onNew
          ? html`
              <button type="button" class="md-btn-filled md-sys-ripple touch-manipulation text-sm" @click=${p.onNew}>
                ${p.newLabel}
              </button>
            `
          : null}
      </div>
      ${p.filter ? shellRecordListFilterToolbar(p.filter) : null}
      ${p.rows.length === 0
        ? emptyFiltered
          ? html`<p class="text-sm text-[color:var(--md-sys-color-on-surface-variant)]">Ningún registro coincide con la búsqueda.</p>`
          : shellEmptyState({
              title: 'Sin registros',
              body: p.emptyText,
              ctaLabel: p.newLabel && p.onNew ? p.newLabel : undefined,
              onCta: p.onNew,
            })
        : html`
            <ul class="shell-record-list grid gap-3 sm:grid-cols-2">
              ${p.rows.map((row, index) => {
                const cardClass = `md-surface-card shell-record-card shell-record-card--${row.syncStatus}`;
                const openable = clickable && Boolean(row.onOpen) && !row.actions;
                const cardContent = shellRecordListCardContent(row, openable);
                return html`
                  <li class="shell-record-list-item" style=${`--shell-stagger-index: ${index}`}>
                    ${openable
                      ? html`
                          <button
                            type="button"
                            class="${cardClass} md-sys-ripple touch-manipulation w-full text-left"
                            aria-label=${`Ver detalle de ${row.headline}`}
                            @click=${row.onOpen}
                          >
                            ${cardContent}
                          </button>
                        `
                      : html`
                          <div class="${cardClass}">
                            ${cardContent}
                            ${row.actions
                              ? html`<div class="shell-record-card__actions">${row.actions}</div>`
                              : null}
                          </div>
                        `}
                  </li>
                `;
              })}
            </ul>
          `}
    </section>
  `;
}
